/**
 * TFK balloon inflation: the pair rule for a balloon and its inflation line.
 * Contract: tfk-contracts/click-and-collect/balloon-inflation.md v1.0.
 * Everything is exported as TFKInflation. The rule and the repair pass take
 * their requests as an argument, so node:test runs them with no browser; the
 * few functions marked browser only read the page. The product page
 * (sections/tfk-product.liquid), the cart drawer
 * (snippets/tfk-cart-drawer.liquid) and the cart page
 * (sections/tfk-cart.liquid) call it.
 *
 * A pair is a balloon variant B and a service variant S. The service units of
 * a pair must equal its inflated balloon units, and the balloon leads: a
 * service line is changed to match its balloons, never the other way round.
 */
(function () {
  'use strict';

  const PROP_INFLATION = 'Inflation';
  const PROP_SERVICE = '_InflationService';
  const PROP_INFLATES = '_Inflates';
  const PROP_FOR = 'For';
  const NOTICE_KEY = 'tfk-inflation-notice';
  // One pass repairs a cart. A second confirms it, or settles what a merge
  // or a balloon turned back to Balloon only left behind.
  const MAX_PASSES = 4;

  // Every sentence a customer reads that this script builds. The fixed words
  // on the product page and the cart are in snippets/tfk-inflation-copy.liquid.
  const COPY = {
    flatNote: '{price}, take it home flat',
    inflatedNote: '{title}, +{charge}',
    breakdown: '{balloon} balloon + {charge} inflation',
    unavailable:
      'Sorry, inflation is not available for {title} at the moment. It is now in your cart as balloon only.',
  };

  const fill = (template, values) =>
    template.replace(/\{(\w+)\}/g, (match, name) => (name in values ? String(values[name]) : match));

  // Ids travel as numeric strings of 1 to 20 digits, never gid:// (contract 3.2).
  const idOf = (value) => {
    const text = value == null ? '' : String(value);
    return /^\d{1,20}$/.test(text) ? text : '';
  };

  const requestId = (id) => (Number.isSafeInteger(Number(id)) ? Number(id) : String(id));

  const prop = (item, name) => {
    const properties = item && item.properties;
    return properties && properties[name] != null ? properties[name] : '';
  };

  const hasText = (value) => typeof value === 'string' && value.trim() !== '';

  // What the cart must change so every pair is in step (contract 3.4).
  // serviceProductIds comes from Liquid: /cart.js carries no metafields, and a
  // line is a service line by its product's tfk.service flag alone.
  function plan(items, serviceProductIds) {
    const lines = items || [];
    const services = new Set((serviceProductIds || []).map(String));
    const isService = (item) => services.has(String(item.product_id));

    const pairs = new Map();
    lines.forEach((item) => {
      if (isService(item)) return;
      const service = idOf(prop(item, PROP_SERVICE));
      if (!service) return;
      const pairKey = `${item.variant_id}:${service}`;
      if (!pairs.has(pairKey)) {
        pairs.set(pairKey, {
          balloon: String(item.variant_id),
          service,
          wanted: 0,
          held: 0,
          title: item.title,
        });
      }
      pairs.get(pairKey).wanted += item.quantity;
    });

    const changes = [];
    lines.forEach((item) => {
      if (!isService(item)) return;
      const pair = pairs.get(`${idOf(prop(item, PROP_INFLATES))}:${item.variant_id}`);
      const keep = pair ? Math.min(item.quantity, pair.wanted - pair.held) : 0;
      if (pair) {
        pair.held += keep;
        if (!pair.line) pair.line = { key: item.key, quantity: item.quantity };
      }
      if (keep !== item.quantity) changes.push({ key: item.key, quantity: keep });
    });

    // A shortfall is added to the pair's own service line when it has one,
    // which is what Shopify's merge of a second add would come to. Only a
    // pair with no service line needs a new line.
    const adds = [];
    pairs.forEach((pair) => {
      if (pair.held >= pair.wanted) return;
      const add = {
        balloon: pair.balloon,
        service: pair.service,
        quantity: pair.wanted - pair.held,
        title: pair.title,
      };
      if (pair.line) add.line = pair.line;
      adds.push(add);
    });
    return { changes, adds };
  }

  const serviceItem = (add) => ({
    id: requestId(add.service),
    quantity: add.quantity,
    properties: { [PROP_INFLATES]: add.balloon, [PROP_FOR]: add.title },
  });

  // The two items of one Inflated add (contract 4.2), or null when any of the
  // four properties would be empty: all four are written or none.
  function pairItems({ balloonId, quantity, properties, service, lineTitle }) {
    const balloon = idOf(balloonId);
    const serviceId = idOf(service && service.id);
    if (!balloon || !serviceId || !hasText(service.title) || !hasText(lineTitle)) return null;
    const kept = {};
    Object.keys(properties || {}).forEach((name) => {
      if (properties[name] != null && properties[name] !== '') kept[name] = properties[name];
    });
    return [
      {
        id: requestId(balloon),
        quantity,
        properties: { ...kept, [PROP_INFLATION]: service.title, [PROP_SERVICE]: serviceId },
      },
      serviceItem({ balloon, service: serviceId, quantity, title: lineTitle }),
    ];
  }

  // Turns one inflated balloon line into a flat line: the same variant and
  // quantity with neither Inflation nor _InflationService (contract 4.5).
  // Shopify merges it into a flat line of the same variant if one is there.
  async function flattenLine(line, request, extras) {
    const properties = {};
    Object.keys(line.properties || {}).forEach((name) => {
      if (name !== PROP_INFLATION && name !== PROP_SERVICE) properties[name] = line.properties[name];
    });
    const flat = { id: requestId(line.variant_id), quantity: line.quantity };
    if (Object.keys(properties).length) flat.properties = properties;

    const removed = await request('/cart/change.js', { id: line.key, quantity: 0, ...extras });
    if (!removed.ok) return false;
    const added = await request('/cart/add.js', { items: [flat], ...extras });
    if (added.ok) return true;
    // The balloon must not be lost: put the line back as it was.
    await request('/cart/add.js', {
      items: [{ id: flat.id, quantity: line.quantity, properties: line.properties }],
      ...extras,
    });
    return false;
  }

  /**
   * Brings every pair in step (contract 4.3) and turns a balloon back to
   * Balloon only when its inflation cannot be added (contract 4.4).
   *
   * - request(path, body): resolves { ok, body }. A body means POST.
   * - cart: a /cart.js or /cart/change.js body already in hand, if any.
   * - flags: the service product ids as Liquid last rendered them.
   * - readFlags(sections): the same ids from a response's rendered sections,
   *   so a line this pass adds is judged by what Liquid says of it.
   * - sections, sectionsUrl: asked of every write, for readFlags and so the
   *   caller can patch the page from the last response.
   *
   * Resolves { ok, changed, added, addedVariants, flattened, cart }. ok: every
   * pair is in step. added: a new service line went in, and addedVariants
   * names its variant. flattened: the titles of balloons turned back to
   * Balloon only. cart: the newest cart body; it carries sections only when
   * the last write was a change.
   */
  async function repair(options) {
    const request = options.request || browserRequest;
    const readFlags = options.readFlags || (() => null);
    const extras = options.sections
      ? { sections: options.sections, sections_url: options.sectionsUrl || '/' }
      : {};
    const result = { ok: false, changed: false, added: false, addedVariants: [], flattened: [], cart: null };
    let flags = (options.flags || []).map(String);
    let cart = options.cart || null;
    const learn = (body) => {
      const next = readFlags(body && body.sections);
      if (next) flags = next.map(String);
    };
    const readCart = async () => {
      const response = await request('/cart.js');
      return response.ok ? response.body : null;
    };

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      if (!cart) cart = await readCart();
      if (!cart) return result;
      const { changes, adds } = plan(cart.items, flags);
      if (!changes.length && !adds.length) {
        result.ok = true;
        result.cart = cart;
        return result;
      }
      result.changed = true;

      for (const change of changes) {
        const response = await request('/cart/change.js', { id: change.key, quantity: change.quantity, ...extras });
        if (!response.ok) return result;
        cart = response.body;
        learn(cart);
      }

      for (const add of adds) {
        let repaired;
        if (add.line) {
          const response = await request('/cart/change.js', {
            id: add.line.key,
            quantity: add.line.quantity + add.quantity,
            ...extras,
          });
          repaired = response.ok;
          if (repaired) {
            cart = response.body;
            learn(cart);
            continue;
          }
        } else {
          const response = await request('/cart/add.js', { items: [serviceItem(add)], ...extras });
          repaired = response.ok;
          if (response.ok) {
            learn(response.body);
            const line = response.body.items && response.body.items[0];
            if (!line || !flags.includes(String(line.product_id))) {
              // What _InflationService names is not a service product, so the
              // units just added are an ordinary product: take them out again.
              repaired = false;
              if (line && !(await takeBack(line.key, add.quantity))) return result;
            }
          }
        }
        if (repaired) {
          result.added = true;
          result.addedVariants.push(add.service);
        } else {
          const current = await readCart();
          if (!current) return result;
          const inflatedLines = current.items.filter(
            (item) =>
              !flags.includes(String(item.product_id)) &&
              String(item.variant_id) === add.balloon &&
              idOf(prop(item, PROP_SERVICE)) === add.service
          );
          for (const line of inflatedLines) {
            if (!(await flattenLine(line, request, extras))) return result;
          }
          result.flattened.push(add.title);
        }
        cart = null; // an add answers with its lines, not with the cart
      }
    }
    return result;

    async function takeBack(key, quantity) {
      const current = await readCart();
      if (!current) return false;
      const line = current.items.find((item) => item.key === key);
      if (!line) return true;
      const response = await request('/cart/change.js', {
        id: key,
        quantity: Math.max(0, line.quantity - quantity),
        ...extras,
      });
      return response.ok;
    }
  }

  // The Balloon only instead link: turns the line with this key flat, then
  // lets the repair remove that pair's service units.
  async function dropInflation(options) {
    const request = options.request || browserRequest;
    const extras = options.sections
      ? { sections: options.sections, sections_url: options.sectionsUrl || '/' }
      : {};
    const failed = (changed) => ({
      ok: false,
      changed,
      added: false,
      addedVariants: [],
      flattened: [],
      cart: null,
    });
    const response = await request('/cart.js');
    if (!response.ok) return failed(false);
    const line = response.body.items.find((item) => item.key === options.key);
    if (line && idOf(prop(line, PROP_SERVICE))) {
      if (!(await flattenLine(line, request, extras))) return failed(true);
      const result = await repair({ ...options, request, cart: null });
      result.changed = true;
      return result;
    }
    return repair({ ...options, request, cart: response.body });
  }

  // ---------- browser only: what the pages call ----------

  // The service product ids Liquid rendered (snippets/tfk-inflation-flags.liquid).
  function flagsIn(root) {
    const el = root && root.querySelector('[data-tfk-service-products]');
    if (!el) return null;
    try {
      const ids = JSON.parse(el.textContent);
      return Array.isArray(ids) ? ids.map(String) : null;
    } catch (error) {
      return null;
    }
  }

  // The same ids from the sections a cart write rendered, so they are as new
  // as the cart that write answered with.
  function flagsFromSections(sections) {
    if (!sections || typeof DOMParser === 'undefined') return null;
    const names = Object.keys(sections);
    for (let index = 0; index < names.length; index += 1) {
      const html = sections[names[index]];
      const flags = html ? flagsIn(new DOMParser().parseFromString(html, 'text/html')) : null;
      if (flags) return flags;
    }
    return null;
  }

  // Runs a repair, or the Balloon only instead link when options.dropKey is
  // set, with the flags the page holds. It keeps the sentences for the cart
  // page, and hands back result.sections: the named sections as they render
  // after the last write, for the caller to patch the page from.
  async function settle(options) {
    const sectionsUrl = options.sectionsUrl || window.location.pathname;
    const full = {
      request: browserRequest,
      readFlags: flagsFromSections,
      flags: (options.cart && flagsFromSections(options.cart.sections)) || flagsIn(document) || [],
      ...options,
      sectionsUrl,
    };
    const result = options.dropKey ? await dropInflation({ ...full, key: options.dropKey }) : await repair(full);
    saveNotices(result.flattened);
    result.sections = (result.cart && result.cart.sections) || null;
    if (result.ok && result.changed && !result.sections && options.sections && options.sections.length) {
      const rendered = await browserRequest(`${sectionsUrl}?sections=${options.sections.join(',')}`);
      if (rendered.ok) result.sections = rendered.body;
    }
    return result;
  }

  const noticeText = (title) => fill(COPY.unavailable, { title });

  const sessionStore = () => {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  };

  // The cart page shows the sentence, and the page may reload on the way
  // there, so the titles wait in session storage until it reads them.
  function saveNotices(titles, storage) {
    const store = storage || sessionStore();
    if (!store || !titles || !titles.length) return;
    try {
      const held = JSON.parse(store.getItem(NOTICE_KEY) || '[]');
      titles.forEach((title) => {
        if (!held.includes(title)) held.push(title);
      });
      store.setItem(NOTICE_KEY, JSON.stringify(held));
    } catch (error) {
      // Storage is blocked: the cart still shows the balloon as Balloon only.
    }
  }

  function takeNotices(storage) {
    const store = storage || sessionStore();
    if (!store) return [];
    try {
      const held = JSON.parse(store.getItem(NOTICE_KEY) || '[]');
      store.removeItem(NOTICE_KEY);
      return Array.isArray(held) ? held : [];
    } catch (error) {
      return [];
    }
  }

  function browserRequest(path, body) {
    const init = body
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }
      : { headers: { Accept: 'application/json' } };
    return fetch(path, init)
      .then((response) => response.json().then((json) => ({ ok: response.ok, body: json })))
      .catch(() => ({ ok: false, body: null }));
  }

  const TFKInflation = {
    COPY,
    PROP_INFLATION,
    PROP_SERVICE,
    PROP_INFLATES,
    PROP_FOR,
    fill,
    idOf,
    plan,
    pairItems,
    repair,
    dropInflation,
    flagsIn,
    flagsFromSections,
    settle,
    noticeText,
    saveNotices,
    takeNotices,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = TFKInflation;
  }
  if (typeof window !== 'undefined') {
    window.TFKInflation = TFKInflation;
  }
})();
